import { HttpHeaders } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { AppApis } from "@app/core/APIs/app-apis";
import { CrudApiOptions, CrudApiService, GenResp } from "@template-ui/services/crud-api.service";
import { catchError, map, Observable, of } from "rxjs";
import {
  ChangeSetPreview,
  ChangeSetPreviewRequest,
  ChangeSetWorkspaceResult,
  toChangeSetFailure,
  toChangeSetSuccess,
} from "./platform-change-set-workspace.models";

const requestHeaders = new HttpHeaders({
  "Content-Type": "application/json",
  Accept: "application/json, text/plain, */*",
  "skip-error-interceptor": "yes",
  "skip-loading-interceptor": "yes",
});

export const platformChangeSetWorkspaceApiOptions: Partial<CrudApiOptions<"body", "json">> = {
  retries: 0,
  feedbackOptions: {
    successMode: "none",
    errorMode: "none",
  },
  getOptions: {
    responseType: "json",
    headers: requestHeaders,
    withCredentials: true,
  },
  postOptions: {
    responseType: "json",
    headers: requestHeaders,
    withCredentials: true,
  },
};

@Injectable({ providedIn: "root" })
export class PlatformChangeSetWorkspaceService {
  private readonly apis = inject(AppApis);
  private readonly crudService = inject(CrudApiService);

  loadPreview(request: ChangeSetPreviewRequest): Observable<ChangeSetWorkspaceResult<ChangeSetPreview>> {
    return this.crudService
      .getbyUrl<ChangeSetPreview>(
        this.apis.core.manual.getPlatformConfigurationChangeSetPreview(request.configurationCode),
        platformChangeSetWorkspaceApiOptions,
      )
      .pipe(
        map((response) => toChangeSetSuccess(unwrapResponse(response))),
        catchError((error: unknown) => of(toChangeSetFailure<ChangeSetPreview>(error))),
      );
  }

  publishTerminologyEntry(entryId: number): Observable<ChangeSetWorkspaceResult<void>> {
    return this.postAction(this.apis.core.manual.postPlatformConfigurationTerminologyEntryPublish(entryId));
  }

  rollbackTerminologyEntry(entryId: number): Observable<ChangeSetWorkspaceResult<void>> {
    return this.postAction(this.apis.core.manual.postPlatformConfigurationTerminologyEntryRollback(entryId));
  }

  private postAction(url: string): Observable<ChangeSetWorkspaceResult<void>> {
    return this.crudService.postByUrl<void>(url, {}, platformChangeSetWorkspaceApiOptions).pipe(
      map(() => toChangeSetSuccess<void>(undefined as void)),
      catchError((error: unknown) => of(toChangeSetFailure<void>(error))),
    );
  }
}

function unwrapResponse<T>(response: GenResp<T> | T | undefined | null): T {
  if (response && typeof response === "object" && "data" in response) {
    return (response as GenResp<T>).data;
  }

  return response as T;
}
